import { computed, ref, unref, watch } from 'vue';

import { bubblingEvents, getUid, omit, onRE, pick, pickWithRest, propsFactory } from '@components/utils';

import type { ComputedRef, PropType, Ref } from 'vue';

export interface IInputSlot {
  id: ComputedRef<string>;
  isDirty: ComputedRef<boolean>;
  isDisabled: ComputedRef<boolean>;
  isReadonly: ComputedRef<boolean>;
  isPure: Ref<boolean>;
  isValid: ComputedRef<boolean | null>;
  isValidating: Ref<boolean>;
  reset: () => void;
  resetValidation: () => void;
  validate: () => Promise<string[]>;
}

export interface IInputProps {
  id: string | undefined;
  label: string | undefined;
  description: string | undefined;
  value: any;
  messages: string[] | string;
  persistentMessages: boolean;
}
export interface IUseInput {
  id: ComputedRef<string>;
  hasLabel: ComputedRef<boolean>;
  hasDescription: ComputedRef<boolean>;
}

export const makeInputProps = propsFactory(
  {
    /**
     * Идентификатор
     */
    id: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Лейбл
     */
    label: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Описание
     */
    description: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Значение
     */
    value: {
      type: [String, Number, Boolean, Array] as PropType<any>,
      default: null
    },
    /**
     * Сообщения подсказки
     */
    messages: {
      type: [Array, String] as PropType<string[] | string>,
      default: () => []
    },
    /**
     * Всегда показывать сообщения и подсказки
     */
    persistentMessages: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  'input'
);

export function useInput(props: IInputProps, slots: Record<string, unknown>, name: string = 'input'): IUseInput {
  const uid = ref(getUid());
  const id = computed(() => props.id || `${name}-${unref(uid)}`);
  const hasLabel = computed(() => !!props.label || !!slots.label);
  const hasDescription = computed(() => !!props.description || !!slots.description);

  return { id, hasLabel, hasDescription };
}

export function useFilteredInputProps<T extends Record<string, unknown>>(props: T, exclude: string[]) {
  const inputProps = ref<Partial<T>>({});
  const excludedProps = ref<Partial<T>>({});

  watch(
    () => props,
    (props) => {
      const [filteredInputProps, filteredExcludedProps] = filterInputProps(props, exclude);

      inputProps.value = filteredInputProps;
      excludedProps.value = filteredExcludedProps;
    },
    { deep: true, immediate: true }
  );

  return { inputProps, excludedProps };
}

export function useFilteredInputAttrs(attrs: Record<string, unknown>) {
  const rootAttrs = ref({});
  const inputAttrs = ref({});

  watch(
    () => attrs,
    (attrs) => {
      const [filteredRootAttrs, filteredInputAttrs] = filterInputAttrs(attrs);

      rootAttrs.value = filteredRootAttrs;
      inputAttrs.value = filteredInputAttrs;
    },
    { deep: true, immediate: true }
  );

  return { rootAttrs, inputAttrs };
}

/**
 * Filter attributes that should be applied to
 * the root element of an input component. Remaining
 * attributes should be passed to the <input> element inside.
 */
export function filterInputAttrs(attrs: Record<string, unknown>) {
  const [events, props] = pickWithRest(attrs, [onRE]);
  const inputEvents = omit(events, bubblingEvents);
  const [rootAttrs, inputAttrs] = pickWithRest(props, ['class', 'style', 'id', /^data-/ as any]);
  Object.assign(rootAttrs, events);
  Object.assign(inputAttrs, inputEvents);
  return [rootAttrs, inputAttrs];
}

/**
 * Filter props for input controls
 * @param props
 * @param exclude
 */
export function filterInputProps(props: Record<string, unknown>, exclude: string[]) {
  return pick(props, exclude).reverse();
}

export const excludeControls = /^(onClick:append|onClick:prepend|onClick:prependInner|onClick:appendInner).*/g;
